import { StatusId, CommandeBC, HistoriqueEvent } from '../types';
import {
  isCommandeExpired,
  canAccepterRefuser,
  canPrestataireEditCatalogue,
} from './commandeConsultation';

// Derivations PURES de l'onglet OFFRE prestataire-BC. Aucune dependance React.
// Forme PRESTA : la commande WRAPPE l'operation (commande.operation.*), ses lignes sont
// dans commande.articles, le flag panier inverse vit sur commande.operation.transferred.
// Gating 100% id-based (enum StatusId), JAMAIS par libelle.
//
// Copie autonome (et adaptee presta) des fonctions pures du gabarit acheteur
// SuiviOperationBCV2/domain/operationOffre.ts : computeOffreState / computeOffreStatusFlags
// (+ refusedByFromHistorique). Les helpers d'articles (formatCommandeArticles / totalArticlesHT)
// et de gating (expired/transferred/hasActed/...) ne sont PAS redupliques : ils sont importes
// de ./commandeConsultation (regle anti-duplication). On reexporte ceux dont l'onglet Offre
// a besoin pour offrir une surface domaine unique au composant.

export {
  formatCommandeArticles,
  totalArticlesHT,
  isCommandeExpired,
  isTransferred,
  prestataireHasActed,
  canAccepterRefuser,
  canPrestataireEditCatalogue,
  computePrestataireStatusBanner,
  buildAdminViewFormData,
} from './commandeConsultation';

// --- Le presta peut-il encore editer son offre technique ? --------------------
// Regle PURE co-localisee avec le gating de l'onglet Offre (a cote de canAccepterRefuser /
// canPrestataireEditCatalogue) : le champ libre "Offre technique" reste saisissable TANT QUE le
// presta n'a pas envoye son offre. Les deux seuls etats ou une decision est encore due de son cote
// sont le panier normal a accepter/refuser et le panier inverse a chiffrer ; on les reutilise donc
// tels quels au lieu de tester des StatusId dans le JSX. Des l'envoi (transfer -> AttenteAnalyse) ou
// l'acceptation (accept -> Valide), comme pour tout etat terminal, la fonction retourne false et le
// composant rend le champ en lecture seule. Miroir front de la garde readonly du back.
export const canPrestataireEditOffreTechnique = (
  commande: CommandeBC,
  chronoExpired = false,
): boolean =>
  canAccepterRefuser(commande, chronoExpired) ||
  canPrestataireEditCatalogue(commande, chronoExpired);

// --- Etat de presentation de l'offre (presta) ---------------------------------
// Calque computeOffreState acheteur (operationOffre.ts l.85-138) mais cote PRESTA :
//   - statut lu sur commande.status?.id (id-based), pas cmd.id_status d'un rang
//   - showBonCommande = Valide(7) ; showRefusMessage = Refuse(8) | Expire(9) | Annule(14)
//   - refusedBy depuis l'historique de la commande (cote presta : sait s'il a refuse lui-meme
//     ou si l'acheteur a refuse / annule)
export interface OffreRefusInfo {
  date: string;
  refusedBy: 'acheteur' | 'prestataire';
  acheteurNom: string;
  prestataireNom: string;
  rang: number;
  isExpired: boolean;
  isCancelled: boolean;
}

export interface OffreState {
  showBonCommande: boolean;
  showRefusMessage: boolean;
  refusInfo: OffreRefusInfo | null;
}

// Calque refusedByFromHistorique acheteur (operationOffre.ts l.91-107), adapte a la forme
// presta : l'historique vit sur commande.historique. Le backend ne fournit qu'un `prefix`
// + `label` (cf. types.HistoriqueEvent) ; on cherche un evenement de refus et on regarde s'il
// porte la marque "acheteur" (sinon -> le presta a refuse lui-meme).
const refusedByFromHistorique = (commande: CommandeBC): 'acheteur' | 'prestataire' => {
  const historique: HistoriqueEvent[] = commande.historique || [];
  const refusEvent = historique.find(
    (h) =>
      h.prefix?.toLowerCase().includes('refus') || h.label?.toLowerCase().includes('refus'),
  );
  if (refusEvent) {
    if (
      refusEvent.label?.toLowerCase().includes('acheteur') ||
      refusEvent.prefix?.toLowerCase().includes('acheteur')
    ) {
      return 'acheteur';
    }
    return 'prestataire';
  }
  return 'prestataire';
};

const acheteurNomOf = (commande: CommandeBC): string =>
  commande.operation?.organisme?.nom ||
  [commande.operation?.acheteur?.prenom, commande.operation?.acheteur?.nom]
    .filter(Boolean)
    .join(' ')
    .trim() ||
  'Acheteur';

const prestataireNomOf = (commande: CommandeBC): string => {
  const entite = commande.contrat?.entite as { nom?: string } | undefined;
  return entite?.nom || 'Prestataire';
};

export const computeOffreState = (commande: CommandeBC, chronoExpired = false): OffreState => {
  const sid = commande.status?.id;

  const isAccepted = sid === StatusId.Valide;
  const isRefused = sid === StatusId.Refuse;
  const isExpired = sid === StatusId.Expire || isCommandeExpired(commande, chronoExpired);
  const isCancelled = sid === StatusId.Annule;
  // Bon de commande visible quand l'offre est acceptee (statut Valide), jamais expire.
  const showBonCommande = isAccepted;
  // Message terminal de refus/expiration/annulation.
  const showRefusMessage = isRefused || isExpired || isCancelled;

  return {
    showBonCommande,
    showRefusMessage,
    refusInfo: showRefusMessage
      ? {
          date: commande.updated_at || '',
          refusedBy: isCancelled ? 'acheteur' : refusedByFromHistorique(commande),
          acheteurNom: acheteurNomOf(commande),
          prestataireNom: prestataireNomOf(commande),
          rang: commande.rang ?? 1,
          isExpired,
          isCancelled,
        }
      : null,
  };
};

// --- Flags d'affichage du statut (presta) -------------------------------------
// Calque computeOffreStatusFlags acheteur (operationOffre.ts l.151-168), id-based :
//   - isAccepted = Valide(7)
//   - isRefused  = Refuse(8)
//   - isExpired  = Expire(9) OU chrono expire
//   - isWaiting  = AttenteAnalyse(13) : offre envoyee, en attente de la decision acheteur
export interface OffreStatusFlags {
  isAccepted: boolean;
  isRefused: boolean;
  isExpired: boolean;
  isWaiting: boolean;
}

export const computeOffreStatusFlags = (
  commande: CommandeBC,
  chronoExpired = false,
): OffreStatusFlags => {
  const sid = commande.status?.id;
  return {
    isAccepted: sid === StatusId.Valide,
    isRefused: sid === StatusId.Refuse,
    isExpired: sid === StatusId.Expire || isCommandeExpired(commande, chronoExpired),
    isWaiting: sid === StatusId.AttenteAnalyse,
  };
};


// Horodatage francais complet : date ET heure. Un refus engage la suite du parcours (relance d'un
// rang, cloture) — l'heure fait partie de la trace, pas du decor.
const horodatage = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

// Message de refus / expiration / annulation vu par le PRESTATAIRE.
//
// Defini UNE fois et consomme par les onglets Consultation ET Offre : les deux affichent
// volontairement le meme etat, et deux textes recopies finiraient par se contredire. Nomme TOUJOURS
// l'auteur et l'horodatage — un bandeau qui dit "refusée" sans dire par qui ni quand oblige a aller
// fouiller l'historique.
export const messageRefusPrestataire = (info: OffreRefusInfo): string => {
  const quand = horodatage(info.date);
  const suffixe = quand ? ` le ${quand}` : '';

  if (info.isCancelled) {
    return `Projet de bon de commande annulé par ${info.acheteurNom || "l'acheteur"}${suffixe}.`;
  }
  if (info.isExpired) {
    return `Votre offre a expiré${suffixe}, faute de réponse dans le délai imparti.`;
  }
  if (info.refusedBy === 'acheteur') {
    return `Votre offre a été refusée par ${info.acheteurNom || "l'acheteur"}${suffixe}.`;
  }
  return `Vous avez refusé cette consultation${suffixe}.`;
};
